import { useEffect, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import { isPg, getSupabase, subscribeRows, subscribeRow } from './supabaseClient';
import { resolvePath } from './pathMap';
import { getCurrentUser } from './authPlane';
import { tenantStamp } from './tenantStamp';

// ---- Data model ------------------------------------------------------------
// The kit ships a neutral demo domain — rename `items` to whatever your app is
// about (trips, projects, albums, …). The domain is DEFINED in the entity
// registry (backend/shared/entities/registry.js → generated rules + the
// generic accessors in data/entities.js); the hand-written helpers below
// carry the demo-specific logic (counter increments, drop links, jobs).
// The PATTERNS are the point:
//
// items/{id}          : name, description, s3Prefix ('items/<id>/'), assetCount,
//                       totalBytes, createdAt
// items/{id}/assets   : name, sizeBytes, contentType, kind, s3Key, status,
//                       meta { status: 'queued'|'running'|'done'|'error' },
//                       lineage {…}, uploadedAt          ← written by uploads,
//                       processed by the backend meta worker (Firestore-as-queue)
// items/{id}/uploads  : live guest-upload progress rows (mirrored by the backend
//                       from drop-link heartbeats)
// dropLinks/{token}   : guest upload links (see createDropLink below)
// jobs/{id}           : fan-out job state + jobs/{id}/tasks/{taskId} telemetry
// jobControls/{jobId} : { action: 'cancel'|'pause'|'resume' } — kill switches
// config/app          : live-tunable backend settings (see shared/liveConfig.js)
// configs/app         : backend-written status (online flag, heartbeat)
//
// Dual data plane: every hook/helper below branches on REACT_APP_DATA_PLANE.
// firebase (default) — the original Firestore code, untouched. supabase — the
// same doc shapes over Postgres doc-tables ({ id, data jsonb } rows, child
// collections flattened with parent-id columns; see data/pathMap.js) with
// postgres_changes realtime. serverTimestamp() becomes Date.now() on the
// supabase plane (the doc shapes already use ms epochs elsewhere).
// -----------------------------------------------------------------------------

const ready = () => firebase.apps.length > 0 && db;
const now = () => firebase.firestore.FieldValue.serverTimestamp();

// ---- Supabase-plane primitives ----------------------------------------------
const isPlainObj = (v) => v && typeof v === 'object' && !Array.isArray(v);

// Firestore `set(..., { merge: true })` merge semantics: deep-merge plain
// objects (maps), replace everything else.
function deepMerge(base, patch) {
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    out[k] = isPlainObj(v) && isPlainObj(base[k]) ? deepMerge(base[k], v) : v;
  }
  return out;
}

function sb() {
  const client = getSupabase();
  if (!client) throw new Error('[supabase] client not configured (missing env vars)');
  return client;
}

function pgThrow(error, ctx) {
  if (error) throw new Error(`[supabase:${ctx}] ${error.message}`);
}

async function pgGetDoc(path) {
  const { table, id } = resolvePath(path);
  const { data, error } = await sb().from(table).select('id,data').eq('id', id).maybeSingle();
  pgThrow(error, `get ${path}`);
  return data ? { id: data.id, ...(data.data || {}) } : null;
}

export async function pgInsert(collectionPath, id, data) {
  const { table, filters } = resolvePath(collectionPath);
  const row = { id, data };
  for (const f of filters) row[f.col] = f.value;
  const { error } = await sb().from(table).insert(row);
  pgThrow(error, `insert ${collectionPath}/${id}`);
  return id;
}

// `set(..., { merge: true })` equivalent — upserts (creates the row if missing).
export async function pgSetMerge(path, patch) {
  const { table, filters, id } = resolvePath(path);
  // Rows are keyed (parents…, id) — address them with every column the path carries.
  const keyOf = () => Object.fromEntries([['id', id], ...filters.map((f) => [f.col, f.value])]);
  const { data: existing, error: readErr } = await sb()
    .from(table).select('data').match(keyOf()).maybeSingle();
  pgThrow(readErr, `read ${path}`);
  const row = { ...keyOf(), data: deepMerge(existing?.data || {}, patch) };
  const { error } = await sb().from(table).upsert(row);
  pgThrow(error, `merge ${path}`);
}

export async function pgDelete(path) {
  const { table, filters, id } = resolvePath(path);
  const { error } = await sb().from(table).delete().match(Object.fromEntries([['id', id], ...filters.map((f) => [f.col, f.value])]));
  pgThrow(error, `delete ${path}`);
}

export function useCollection(path, { orderBy, dir = 'desc' } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isPg()) {
      let resolved;
      try {
        resolved = resolvePath(path);
      } catch (e) {
        setError(e.message);
        setDocs([]);
        return undefined;
      }
      return subscribeRows(resolved.table, resolved.filters, { orderBy, dir }, setDocs, (msg) => {
        setError(msg);
        setDocs([]);
      });
    }
    if (!ready()) {
      setDocs([]);
      return undefined;
    }
    let ref = db.collection(path);
    if (orderBy) ref = ref.orderBy(orderBy, dir);
    const unsub = ref.onSnapshot(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        setError(err.message);
        setDocs([]);
      }
    );
    return () => unsub();
  }, [path, orderBy, dir]);

  return { docs, error };
}

export function useDoc(path) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isPg()) {
      if (!path) return undefined;
      let resolved;
      try {
        resolved = resolvePath(path);
      } catch (e) {
        setError(e.message);
        return undefined;
      }
      return subscribeRow(resolved.table, resolved.id, setDoc, (msg) => setError(msg), resolved.filters);
    }
    if (!ready() || !path) return undefined;
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [path]);

  return { doc, error };
}

// ---- Items (the demo domain) ------------------------------------------------
export const useItems = () => useCollection('items', { orderBy: 'createdAt' });
export const useItem = (id) => useDoc(id ? `items/${id}` : null);

export function useItemAssets(itemId) {
  return useCollection(itemId ? `items/${itemId}/assets` : 'items', {
    orderBy: 'uploadedAt',
  });
}

// Live, in-flight uploads (mirrored by the backend from drop-link guests'
// progress heartbeats). Lets the owner watch a contributor's upload fill in
// before the finished file lands as a real asset.
export function useItemUploads(itemId) {
  return useCollection(itemId ? `items/${itemId}/uploads` : 'items', {
    orderBy: 'updatedMs',
  });
}

// Dismiss a stale/abandoned in-progress upload row from the owner's view.
export async function deleteItemUpload(itemId, id) {
  if (isPg()) {
    if (!itemId || !id) return;
    await pgDelete(`items/${itemId}/uploads/${id}`).catch(() => {});
    return;
  }
  if (!ready() || !itemId || !id) return;
  await db.collection('items').doc(itemId).collection('uploads').doc(id).delete().catch(() => {});
}

export async function createItem({ name, description = '' }) {
  // Tenancy: stamp the current org/workspace (tenantStamp.js) so the rules'
  // tenantOk() scopes the item; {} = legacy open doc when no org is active.
  if (isPg()) {
    const id = crypto.randomUUID();
    await pgInsert('items', id, {
      name: name || 'Untitled',
      description,
      s3Prefix: `items/${id}/`,
      assetCount: 0,
      totalBytes: 0,
      createdAt: Date.now(),
      ...tenantStamp(),
    });
    return id;
  }
  const ref = db.collection('items').doc();
  await ref.set({
    name: name || 'Untitled',
    description,
    s3Prefix: `items/${ref.id}/`,
    assetCount: 0,
    totalBytes: 0,
    createdAt: now(),
    ...tenantStamp(),
  });
  return ref.id;
}

export async function updateItem(itemId, patch) {
  if (isPg()) {
    await pgSetMerge(`items/${itemId}`, patch);
    return;
  }
  await db.collection('items').doc(itemId).set(patch, { merge: true });
}

export async function deleteItem(itemId) {
  if (isPg()) {
    await pgDelete(`items/${itemId}`);
    return;
  }
  await db.collection('items').doc(itemId).delete();
}

// ---- Assets ------------------------------------------------------------------
// Records a finished OWNER upload as an asset (guests go through the backend's
// /drop/:token/record instead, which mirrors this shape via the admin SDK —
// keep the two in sync). Queues the backend meta worker via meta.status.
export async function recordAsset(itemId, asset) {
  if (isPg()) {
    const me = await getCurrentUser();
    const id = crypto.randomUUID();
    await pgInsert(`items/${itemId}/assets`, id, {
      name: asset.name,
      sizeBytes: asset.sizeBytes || 0,
      contentType: asset.contentType || '',
      kind: asset.kind || 'file',
      s3Key: asset.s3Key,
      status: asset.status || 'stored',
      meta: { status: 'queued' },
      lineage: {
        origin: 'upload',
        by: me?.email || '',
        originalName: asset.name,
        addedAt: Date.now(),
        ...(asset.uploaderMeta ? { uploader: asset.uploaderMeta } : {}),
      },
      tags: Array.isArray(asset.tags) ? asset.tags.slice(0, 30) : [],
      ...(asset.folderPrefix ? { folderPrefix: asset.folderPrefix } : {}),
      ...(Array.isArray(asset.parts) ? { parts: asset.parts } : {}),
      uploadedAt: Date.now(),
    });
    // Counter increments without an RPC: fetch-then-update. Two concurrent
    // uploads can race and drop a count (no free equivalent of Firestore's
    // atomic increment here) — acceptable for the kit; move to a Postgres
    // function/RPC if exact counters ever matter.
    const item = await pgGetDoc(`items/${itemId}`);
    await pgSetMerge(`items/${itemId}`, {
      assetCount: (item?.assetCount || 0) + 1,
      totalBytes: (item?.totalBytes || 0) + (asset.sizeBytes || 0),
    });
    return id;
  }
  const ref = db.collection('items').doc(itemId).collection('assets').doc();
  await ref.set({
    name: asset.name,
    sizeBytes: asset.sizeBytes || 0,
    contentType: asset.contentType || '',
    kind: asset.kind || 'file',
    s3Key: asset.s3Key,
    status: asset.status || 'stored',
    meta: { status: 'queued' },
    lineage: {
      origin: 'upload',
      by: firebase.auth().currentUser?.email || '',
      originalName: asset.name,
      addedAt: Date.now(),
      ...(asset.uploaderMeta ? { uploader: asset.uploaderMeta } : {}),
    },
    tags: Array.isArray(asset.tags) ? asset.tags.slice(0, 30) : [],
    ...(asset.folderPrefix ? { folderPrefix: asset.folderPrefix } : {}),
    ...(Array.isArray(asset.parts) ? { parts: asset.parts } : {}),
    uploadedAt: now(),
  });
  await db.collection('items').doc(itemId).set(
    {
      assetCount: firebase.firestore.FieldValue.increment(1),
      totalBytes: firebase.firestore.FieldValue.increment(asset.sizeBytes || 0),
    },
    { merge: true }
  );
  return ref.id;
}

export async function renameAsset(itemId, assetId, name) {
  if (isPg()) {
    await pgSetMerge(`items/${itemId}/assets/${assetId}`, { name });
    return;
  }
  await db.collection('items').doc(itemId).collection('assets').doc(assetId)
    .set({ name }, { merge: true });
}

export async function deleteAssetDoc(itemId, assetId) {
  if (isPg()) {
    await pgDelete(`items/${itemId}/assets/${assetId}`);
    return;
  }
  await db.collection('items').doc(itemId).collection('assets').doc(assetId).delete();
}

// ---- Fan-out jobs -------------------------------------------------------------
// One job doc + a tasks subcollection with live telemetry per prep/chunk/stitch.
export function useJob(jobId) {
  const [job, setJob] = useState(undefined);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (isPg()) {
      if (!jobId) {
        setJob(undefined);
        setTasks([]);
        return undefined;
      }
      const jobRef = resolvePath(`jobs/${jobId}`);
      const taskRef = resolvePath(`jobs/${jobId}/tasks`);
      const unsubJob = subscribeRow(jobRef.table, jobRef.id, setJob, () => setJob(null));
      const unsubTasks = subscribeRows(
        taskRef.table,
        taskRef.filters,
        { orderBy: 'index', dir: 'asc' },
        setTasks,
        () => setTasks([])
      );
      return () => {
        unsubJob();
        unsubTasks();
      };
    }
    if (!ready() || !jobId) {
      setJob(undefined);
      setTasks([]);
      return undefined;
    }
    const unsubJob = db.collection('jobs').doc(jobId).onSnapshot(
      (snap) => setJob(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setJob(null)
    );
    const unsubTasks = db
      .collection('jobs')
      .doc(jobId)
      .collection('tasks')
      .onSnapshot(
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
          setTasks(items);
        },
        () => setTasks([])
      );
    return () => {
      unsubJob();
      unsubTasks();
    };
  }, [jobId]);

  return { job, tasks };
}

// All fan-out jobs (live + recent), newest first, for the Jobs page.
export function useJobs(max = 100, enabled = true) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    if (isPg()) {
      if (!enabled) {
        setJobs(null);
        return undefined;
      }
      const { table } = resolvePath('jobs');
      return subscribeRows(
        table,
        [],
        { orderBy: 'createdAt', dir: 'desc', limit: max },
        setJobs,
        () => setJobs([])
      );
    }
    if (!ready() || !enabled) {
      setJobs(null);
      return undefined;
    }
    const unsub = db.collection('jobs').onSnapshot(
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setJobs(items.slice(0, max));
      },
      () => setJobs([])
    );
    return unsub;
  }, [max, enabled]);
  return jobs;
}

const JOB_ACTIVE = ['queued', 'planning', 'running', 'stitching'];
export const jobIsActive = (job) => JOB_ACTIVE.includes(job?.status) || !!job?.paused;

// Queue a fan-out job for the backend orchestrator to adopt. `type` selects the
// role handlers in backend/runJob.js; `spec` is the job's input (kept small —
// big inputs belong in S3). Example: queueFanoutJob('manifest', { prefix:
// 'items/abc/' }).
export async function queueFanoutJob(type, spec, { title = '' } = {}) {
  if (isPg()) {
    const me = await getCurrentUser();
    const id = `${type}-${Date.now()}`;
    await pgInsert('jobs', id, {
      kind: 'fanout',
      type,
      spec,
      title: title || null,
      status: 'queued',
      progress: 0,
      createdAt: Date.now(),
      requestedBy: me?.email || '',
    });
    return id;
  }
  const ref = db.collection('jobs').doc(`${type}-${Date.now()}`);
  await ref.set({
    kind: 'fanout',
    type,
    spec,
    title: title || null,
    status: 'queued',
    progress: 0,
    createdAt: Date.now(),
    requestedBy: firebase.auth().currentUser?.email || '',
  });
  return ref.id;
}

// Write a control request the orchestrator watches. action: cancel|pause|resume.
// This is THE kill switch — every long-running backend job must honor it.
export async function controlJob(jobId, action) {
  if (isPg()) {
    if (!jobId) return;
    const me = await getCurrentUser();
    await pgSetMerge(`jobControls/${jobId}`, {
      action,
      requestedAt: Date.now(),
      by: me?.email || 'admin',
    });
    return;
  }
  if (!ready() || !jobId) return;
  await db.collection('jobControls').doc(jobId).set(
    { action, requestedAt: Date.now(), by: firebase.auth().currentUser?.email || 'admin' },
    { merge: true }
  );
}
export const cancelJob = (jobId) => controlJob(jobId, 'cancel');
export const pauseJob = (jobId) => controlJob(jobId, 'pause');
export const resumeJob = (jobId) => controlJob(jobId, 'resume');

// Delete a finished/errored job doc and its per-task telemetry, so the Jobs
// page can be tidied. (The orchestrator never touches done/error jobs, so
// removing them is safe.)
export async function deleteJob(jobId) {
  if (isPg()) {
    if (!jobId) return;
    const tasks = resolvePath(`jobs/${jobId}/tasks`);
    let q = sb().from(tasks.table).delete();
    for (const f of tasks.filters) q = q.eq(f.col, f.value);
    const { error } = await q;
    pgThrow(error, `delete jobs/${jobId}/tasks`);
    await pgDelete(`jobs/${jobId}`);
    return;
  }
  if (!ready() || !jobId) return;
  const jobRef = db.collection('jobs').doc(jobId);
  const tasks = await jobRef.collection('tasks').get();
  // Tasks subcollections are small (a few chunks + prep/stitch); one batch is
  // plenty, but chunk by 450 just in case.
  let batch = db.batch();
  let n = 0;
  for (const d of tasks.docs) {
    batch.delete(d.ref);
    n += 1;
    if (n % 450 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
      batch = db.batch();
    }
  }
  await batch.commit();
  await jobRef.delete();
}

// Bulk-delete every errored job. Returns the number removed.
export async function clearErroredJobs() {
  if (isPg()) {
    const client = getSupabase();
    if (!client) return 0;
    const { table } = resolvePath('jobs');
    const { data, error } = await client.from(table).select('id').eq('data->>status', 'error');
    pgThrow(error, 'list errored jobs');
    for (const row of data || []) {
      // eslint-disable-next-line no-await-in-loop
      await deleteJob(row.id);
    }
    return (data || []).length;
  }
  if (!ready()) return 0;
  const snap = await db.collection('jobs').where('status', '==', 'error').get();
  for (const d of snap.docs) {
    // eslint-disable-next-line no-await-in-loop
    await deleteJob(d.id);
  }
  return snap.size;
}

// ---- Guest drop links ----------------------------------------------------------
// dropLinks/{token} : itemId, itemName, label, ownerName, passcode (''|code),
//   status ('active'|'revoked'), createdAt, expiresAt (ms epoch),
//   uploadedBytes, uploadedCount, lastUploadAt, lastContributor.
// A contributor with the link uploads through the backend (token-auth), which
// records assets into the item via the admin SDK — guests never touch Firestore.
function randomToken(bytes = 18) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function createDropLink(
  item,
  { label = '', passcode = '', expiresDays = 14, guestName = '', guestNote = '' } = {}
) {
  const token = randomToken();
  if (isPg()) {
    const me = await getCurrentUser();
    const ownerName = me?.displayName || (me?.email ? me.email.split('@')[0] : '') || '';
    await pgInsert('dropLinks', token, {
      token,
      itemId: item.id,
      itemName: item.name || '',
      label: label || '',
      ownerName,
      guestName: guestName || '',
      guestNote: guestNote || '',
      passcode: passcode || '',
      status: 'active',
      createdAt: Date.now(),
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
      uploadedBytes: 0,
      uploadedCount: 0,
    });
    return token;
  }
  const me = firebase.auth().currentUser;
  const ownerName = me?.displayName || (me?.email ? me.email.split('@')[0] : '') || '';
  await db
    .collection('dropLinks')
    .doc(token)
    .set({
      token,
      itemId: item.id,
      itemName: item.name || '',
      label: label || '',
      ownerName,
      guestName: guestName || '',
      guestNote: guestNote || '',
      passcode: passcode || '',
      status: 'active',
      createdAt: now(),
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
      uploadedBytes: 0,
      uploadedCount: 0,
    });
  return token;
}

export function useDropLinks(itemId) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (isPg()) {
      if (!itemId) {
        setLinks([]);
        return undefined;
      }
      // itemId lives inside the jsonb doc (not a flattened parent column), so
      // filter client-side via `match`; createdAt is a ms epoch on this plane.
      const { table } = resolvePath('dropLinks');
      return subscribeRows(
        table,
        [],
        { match: (d) => d.itemId === itemId, orderBy: 'createdAt', dir: 'desc' },
        setLinks,
        () => setLinks([])
      );
    }
    if (!ready() || !itemId) {
      setLinks([]);
      return undefined;
    }
    const unsub = db
      .collection('dropLinks')
      .where('itemId', '==', itemId)
      .onSnapshot(
        (snap) => {
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setLinks(docs);
        },
        () => setLinks([])
      );
    return unsub;
  }, [itemId]);
  return links;
}

export async function revokeDropLink(token) {
  if (isPg()) {
    await pgSetMerge(`dropLinks/${token}`, { status: 'revoked' });
    return;
  }
  await db.collection('dropLinks').doc(token).set({ status: 'revoked' }, { merge: true });
}

export async function reactivateDropLink(token, { expiresDays = 14 } = {}) {
  if (isPg()) {
    await pgSetMerge(`dropLinks/${token}`, {
      status: 'active',
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
    });
    return;
  }
  await db
    .collection('dropLinks')
    .doc(token)
    .set({ status: 'active', expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000 }, { merge: true });
}

export async function deleteDropLink(token) {
  if (isPg()) {
    await pgDelete(`dropLinks/${token}`);
    return;
  }
  await db.collection('dropLinks').doc(token).delete();
}

// ---- Live config (config/app) ---------------------------------------------------
// Client-editable runtime knobs the backend deep-merges over its defaults (see
// backend/shared/liveConfig.js). ANYTHING an operator may want to tune at
// runtime belongs there, never in a redeploy.
export const useLiveConfig = () => useDoc('config/app');
export async function saveLiveConfig(patch) {
  if (isPg()) {
    await pgSetMerge('config/app', patch);
    return;
  }
  await db.collection('config').doc('app').set(patch, { merge: true });
}

// Backend-written status (online flag + heartbeat) for a health indicator.
// (On the supabase plane 'configs/app' resolves to the config table's 'status'
// row — see pathMap.js.)
export const useBackendStatus = () => useDoc('configs/app');
